import type { LitNodeClientNodeJs } from "@lit-protocol/lit-node-client"
import type { LIT_NETWORKS_KEYS, SessionSigsMap } from "@lit-protocol/types"
import { getPkpAddressFromSessionSigs } from "../internal/common/pkp"
import { createNestedUnifiedAccessControlConditions } from "../internal/common/access-control-conditions"
import { LIT_ACTION_CIDS, RUNNER_SECRET_IPFS_HASHES, VAULT_SECRET_IPFS_HASHES } from "../../ipfs-constants"
import { getFirstSessionSig } from "../internal/common"
import { storePrivateKey } from "@lit-protocol/wrapped-keys/src/lib/service-client/client"
import { LIT_ACTIONS_VERSION } from "../constants"

export type DeriveKeyFromRootResult = {
    id: string
    pkpAddress: string
    generatedPublicKey: string
  }

export type DeriveKeyFromRootParams = {
    litNodeClient: LitNodeClientNodeJs
    pkpSessionSigs: SessionSigsMap
    rootKeyId: string
    keyDomainPrefix: string
    derivationContext?: string
    memo: string
  }

/**
 * Derives a key from a root key using domain separation and stores it as a wrapped key.
 * This handler wraps the deriveKeyFromRoot Lit Action to provide a clean API.
 *
 * The Lit Action fetches the root key internally using the rootKeyId, so callers
 * only need to pass the ID instead of ciphertext and access control conditions.
 *
 * @param litNodeClient - Lit Node Client instance
 * @param pkpSessionSigs - PKP session signatures
 * @param rootKeyId - ID of the root key in Lit storage
 * @param pkpAddress - PKP address that owns the root key
 * @param keyDomainPrefix - Domain prefix for key separation (e.g., VAULT_KEY_DOMAIN_PREFIX, RUNNER_PARENT_KEY_DOMAIN_PREFIX)
 * @param derivationContext - Optional context for domain separation (e.g., 'user-vault')
 * @param memo - Memo to identify this derived wrapped key
 * @returns Stored wrapped key with id, pkpAddress, and generatedPublicKey
 */
export async function deriveKeyFromRoot({
    litNodeClient,
    pkpSessionSigs,
    rootKeyId,
    keyDomainPrefix,
    derivationContext = LIT_ACTIONS_VERSION,
    memo,
  }: DeriveKeyFromRootParams): Promise<DeriveKeyFromRootResult> {
    const pkpAddress = getPkpAddressFromSessionSigs(Object.values(pkpSessionSigs))

    // biome-ignore lint/suspicious/noExplicitAny: TODO: type this properly once we remove the feature flag conditional logic
    const derivedKeyAccessControlConditions: any[] =
      createNestedUnifiedAccessControlConditions(
        pkpAddress,
        RUNNER_SECRET_IPFS_HASHES,
      )
  
    // Execute the deriveKeyFromRoot Lit Action
    const result = await litNodeClient.executeJs({
      sessionSigs: pkpSessionSigs,
      ipfsId: LIT_ACTION_CIDS.deriveKeyFromRoot,
      jsParams: {
        rootKeyId,
        pkpAddress,
        litNetwork: litNodeClient.config.litNetwork as LIT_NETWORKS_KEYS,
        sessionSig: getFirstSessionSig(pkpSessionSigs),
        keyDomainPrefix,
        derivationContext,
        derivedKeyAccessControlConditions,
        vaultIpfsHashes: VAULT_SECRET_IPFS_HASHES,
      },
    })
  
    // Validate the result
    if (!result?.success || !result?.response) {
      throw new Error(`Lit Action execution failed: ${JSON.stringify(result)}`)
    }
  
    if (
      typeof result.response === 'string' &&
      result.response.startsWith('Error:')
    ) {
      throw new Error(`Error executing the Lit Action: ${result.response}`)
    }
  
    // Parse the derived key from Lit Action
    let derivedKey: {
      ciphertext: string
      dataToEncryptHash: string
      publicKey: string
    }
    try {
      derivedKey = JSON.parse(result?.response as string)
    } catch (error) {
      throw new Error(
        `Failed to parse response from deriveKeyFromRoot: ${error instanceof Error ? error.message : String(error)}. Response: ${result?.response}`,
      )
    }
  
    // Store the derived key as a wrapped key
    const { id } = await storePrivateKey({
      sessionSig: getFirstSessionSig(pkpSessionSigs),
      storedKeyMetadata: {
        ciphertext: derivedKey.ciphertext,
        publicKey: derivedKey.publicKey,
        keyType: 'K256',
        dataToEncryptHash: derivedKey.dataToEncryptHash,
        memo,
      },
      litNetwork: litNodeClient.config.litNetwork,
    })
  
    // Return only the wrapped key metadata
    return {
      id,
      pkpAddress,
      generatedPublicKey: derivedKey.publicKey,
    }
  }
  